// XSM-1536 - SSO Login flow
// Author: Abhishek Bharti
import { useCallback } from "react";
import { concatAndEncodeBase64 } from "../utils";

const XSM_BASE_URL = process.env.REACT_APP_XSM_BASE_URL as string;
const XSM_PRODUCT_URL = process.env.REACT_APP_XSM_PRODUCT_URL as string;
const WS02_KEY = process.env.REACT_APP_WS02_KEY as string;
const WS02_SECRET = process.env.REACT_APP_WS02_SECRET as string;
const COMMON_CALLBACK_URL = process.env.REACT_APP_COMMON_CALLBACK_URL as string;

type TokenType = "access_token" | "refresh_token";

type LogoutOptions = {
  redirectToLogoutEndpoint?: boolean;
};

export const useLogout = () => {
  const revokeToken = useCallback(
    async (token: string | null | undefined, tokenType: TokenType) => {
      if (!token) {
        return true;
      }

      if (!XSM_BASE_URL || !WS02_KEY || !WS02_SECRET) {
        console.warn("Missing configuration for token revocation.");
        return false;
      }

      const basicAuthorizationSecret = concatAndEncodeBase64(
        WS02_KEY as string,
        WS02_SECRET as string,
      );

      try {
        const response = await fetch(
          `${XSM_BASE_URL}/xsm-api/v1/proxy/oauth2/revoke`,
          {
            method: "POST",
            headers: {
              "XSM-Domain": localStorage.getItem("domain") || "",
              "Content-Type": "application/x-www-form-urlencoded",
              Authorization: `Basic ${basicAuthorizationSecret}`,
            },
            body: new URLSearchParams({
              token,
              token_type_hint: tokenType,
            }),
          },
        );

        if (!response.ok) {
          throw new Error(
            `Failed to revoke token (${tokenType}): ${response.status}`,
          );
        }

        return true;
      } catch (error) {
        console.error("Error revoking token:", error);
        return false;
      }
    },
    [],
  );

  const handleLogout = useCallback(
    async ({ redirectToLogoutEndpoint = false }: LogoutOptions = {}) => {
      const accessToken = localStorage.getItem("access_token");
      const refreshToken = localStorage.getItem("refresh_token");
      const idToken = localStorage.getItem("id_token");
      const domain = localStorage.getItem("domain") || "DynaChrgOps";

      try {
        await Promise.all([
          revokeToken(accessToken, "access_token"),
          revokeToken(refreshToken, "refresh_token"),
        ]);
      } catch (error) {
        console.error("Error during token revocation:", error);
      }

      localStorage.clear();

      if (!XSM_BASE_URL) {
        window.open(COMMON_CALLBACK_URL, "_self");
        return;
      }

      const url = new URL(`${XSM_BASE_URL}/xsm-api-sso/v2/proxy/oidc/logout`);
      const queryParams = new URLSearchParams({
        post_logout_redirect_uri: redirectToLogoutEndpoint
          ? COMMON_CALLBACK_URL
          : XSM_PRODUCT_URL,
      });

      if (domain) {
        queryParams.set("XSM-Domain", domain);
      }

      if (idToken) {
        queryParams.set("id_token_hint", idToken);
      }

      url.search = queryParams.toString();

      // Full logout (or single-product Exit) → OIDC logout endpoint
      // Multi-product Exit → common product selection page
      if (redirectToLogoutEndpoint) {
        window.location.assign(url.toString());
      } else {
        window.open(COMMON_CALLBACK_URL, "_self");
      }
    },
    [revokeToken],
  );

  return { handleLogout, revokeToken };
};

export default useLogout;
